import { useState } from 'react';
import { toast } from 'react-toastify';
import { APIs } from '../config/apis';
import { 
  searchCobranca as searchCobrancaService, 
  searchCobrancaHybrid,
  searchUniversal,
  searchExternalApis 
} from '../services/apiService';
import { BACKEND_CONFIG } from '../config/backend';

export const useSearch = () => {
  const [searchInputs, setSearchInputs] = useState({});
  const [searchResults, setSearchResults] = useState({});
  const [loading, setLoading] = useState({});
  const [expandedResults, setExpandedResults] = useState({});

  // Busca unificada (baseada no projeto de referência)
  const searchCobranca = async (apiKey, customCobrancaId = null) => {
    const cobrancaId = customCobrancaId || searchInputs[apiKey]?.trim();
    if (!cobrancaId) {
      toast.warning('Digite um ID de cobrança');
      return;
    }

    // Limpar dados antigos da API específica ANTES de iniciar nova busca
    const api = APIs[apiKey];
    setSearchResults(prev => ({
      ...prev,
      [apiKey]: {
        found: null, // null indica que ainda não foi determinado
        data: null,
        error: null,
        status: 'searching',
        name: api.name,
        color: api.color
      }
    }));
    
    setLoading(prev => ({ ...prev, [apiKey]: true }));
    
    // Timeout geral para a busca (30 segundos)
    const searchTimeout = setTimeout(() => {
      setLoading(prev => ({ ...prev, [apiKey]: false }));
      toast.error('Timeout na busca. Tente novamente.');
    }, 30000);

    try {
      const result = {
        name: api.name,
        color: api.color,
        found: false,
        data: null,
        rawData: null, // Dados brutos para exibição completa
        error: null,
        status: 'searching',
        cobrancaId: cobrancaId,
        cleanCobrancaId: cobrancaId,
        timestamp: new Date().toISOString()
      };
      
      try {
        // Para a API principal, usar o novo serviço de busca inteligente
        if (apiKey === 'apiBancoBitrix') {
          const searchResult = await searchCobrancaService(cobrancaId);
          
          console.log('🔍 Resultado do searchCobrancaService:', searchResult);
          
          if (searchResult.success && searchResult.data) {
            result.found = true;
            result.data = searchResult.data;
            result.rawData = searchResult.data;
            result.status = 'found';
            result.testedParcelas = searchResult.testedParcelas;
            result.foundParcela = searchResult.parcela;
            result.endpoint = searchResult.endpoint;
            result.searchMethod = searchResult.searchMethod;
            result.searchSteps = searchResult.searchSteps;
            result.searchSource = searchResult.searchSource; // ORIGEM DOS DADOS
            result.searchType = searchResult.searchType; // TIPO DE BUSCA
            result.executionTime = searchResult.executionTime; // TEMPO DE EXECUÇÃO
            
            // ✅ LOG SIMPLES DA ORIGEM
            const origem = searchResult.searchSource === 'local_database' ? '🗄️ BANCO LOCAL' : 
                          searchResult.searchSource === 'api_fallback' ? '🌐 API EXTERNA' :
                          searchResult.searchSource === 'api_and_saved_to_local' ? '🌐➡️🗄️ API → BANCO' : 
                          '❓ ORIGEM DESCONHECIDA';
            console.log(`📊 ORIGEM: ${origem} (${searchResult.executionTime || 0}ms)`);
            
            // Expandir automaticamente os detalhes
            setExpandedResults(prev => ({
              ...prev,
              [apiKey]: true
            }));
            
            toast.success(`✅ Encontrado na API Bitrix: ${searchResult.parcela || cobrancaId} (${searchResult.searchMethod})`);
          } else {
            result.error = 'Cobrança não encontrada';
            result.status = 'not_found';
            result.testedParcelas = searchResult.testedParcelas;
            result.searchSteps = searchResult.searchSteps;
          }
        } else {
          // Para outras APIs, usar proxy do backend
          try {
            // Mapear API key para endpoint do proxy
            const proxyMap = {
              'apiRegistroInteresse': 'registro-interesse',
              'apiBitrixComentarios': 'comentarios',
              'apiBitrixComentarioNovo': 'comentarios',
              'apiRegistroConversa': 'conversas'
            };
            
            const proxyEndpoint = proxyMap[apiKey];
            if (!proxyEndpoint) {
              result.error = 'API não suportada pelo proxy';
              result.status = 'error';
              return;
            }
            
            // Usar endpoint correto baseado na API
            let endpointPath;
            if (apiKey === 'apiRegistroInteresse') {
              endpointPath = 'json'; // API Registro Interesse usa /json
            } else {
              endpointPath = 'registroInteresse'; // Outras APIs usam /registroInteresse
            }
            
            const response = await fetch(`${BACKEND_CONFIG.BASE_URL}/api/proxy/external/${proxyEndpoint}/${endpointPath}?id=${encodeURIComponent(cobrancaId)}`, {
              method: 'GET',
              headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
              }
            });
            
            if (response.status === 200) {
              const data = await response.json();
              if (data.success && data.data) {
                result.found = true;
                result.data = data.data;
                result.rawData = data.data; // Armazenar dados brutos
                result.endpoint = endpointPath;
                result.status = 'found';
                result.source = 'proxy';
                
                setExpandedResults(prev => ({
                  ...prev,
                  [apiKey]: true
                }));
              }
            } else if (response.status === 404) {
              result.error = 'Arquivo não encontrado';
              result.status = 'not_found';
            } else if (response.status === 503) {
              result.error = 'API externa não está respondendo';
              result.status = 'error';
            } else if (response.status === 408) {
              result.error = 'Timeout na consulta da API externa';
              result.status = 'error';
            } else {
              const errorData = await response.json().catch(() => ({}));
              result.error = errorData.message || `Erro ${response.status}`;
              result.status = 'error';
            }
          } catch (proxyError) {
            console.error('Erro no proxy:', proxyError);
            result.error = `Erro na consulta: ${proxyError.message}`;
            result.status = 'error';
          }
        }
      } catch (error) {
        console.error('Erro na busca:', error);
        result.error = `Erro na busca: ${error.message}`;
        result.status = 'error';
      }

      // Salvar resultado
      setSearchResults(prev => ({
        ...prev,
        [apiKey]: {
          ...result,
          found: result.found || false,
          data: result.data || null,
          error: result.error || null,
          status: result.status || 'error',
          name: api.name,
          color: api.color
        }
      }));

    } catch (error) {
      console.error('Erro geral na busca:', error);
      toast.error('Erro ao realizar busca');
    } finally {
      clearTimeout(searchTimeout);
      setLoading(prev => ({ ...prev, [apiKey]: false }));
    }
  };

  // Buscar em todas as APIs
  const searchAllApis = async (customCobrancaId = null) => {
    const cobrancaId = customCobrancaId || Object.values(searchInputs).find(input => input?.trim());
    if (!cobrancaId) {
      toast.warning('Digite um ID de cobrança');
      return;
    }

    // Limpar todos os resultados
    setSearchResults({});
    setExpandedResults({});
    
    // Buscar em todas as APIs
    const apiKeys = Object.keys(APIs);
    const promises = apiKeys.map(apiKey => searchCobranca(apiKey, cobrancaId));
    
    try {
      await Promise.all(promises);
      toast.success('Busca em todas as APIs concluída!');
    } catch (error) {
      console.error('Erro na busca em todas as APIs:', error);
      toast.error('Erro ao buscar em todas as APIs');
    }
  };

  // Busca híbrida inteligente (banco local + API)
  const searchHybrid = async (cobrancaId) => {
    if (!cobrancaId?.trim()) {
      toast.warning('Digite um ID de cobrança');
      return null;
    }

    setLoading(prev => ({ ...prev, hybrid: true }));
    
    try {
      console.log(`🔍 Iniciando busca híbrida para: ${cobrancaId}`);
      const result = await searchCobrancaHybrid(cobrancaId);
      
      if (result.success) {
        toast.success(`✅ Encontrado via ${result.searchSource}! Tempo: ${result.executionTime}ms`);
        
        // Mostrar informações sobre o método usado
        const sourceText = result.searchSource === 'local_database' ? '🗄️ Banco Local' : '🌐 API Externa';
        console.log(`📊 Resultado da busca híbrida: ${sourceText} (${result.searchMethod})`);
      } else {
        toast.error(`❌ Não encontrado: ${result.message}`);
      }
      
      return result;
    } catch (error) {
      console.error('❌ Erro na busca híbrida:', error);
      toast.error('Erro na busca híbrida');
      return null;
    } finally {
      setLoading(prev => ({ ...prev, hybrid: false }));
    }
  };

  // Busca universal (PC, CNPJ ou CPF)
  const searchUniversalTerm = async (searchTerm) => {
    if (!searchTerm?.trim()) {
      toast.warning('Digite um termo de busca (PC, CNPJ ou CPF)');
      return null;
    }

    setLoading(prev => ({ ...prev, universal: true }));
    
    try {
      console.log(`🔍 Iniciando busca universal para: ${searchTerm}`);
      const result = await searchUniversal(searchTerm);
      
      if (result.success) {
        const typeText = result.searchType === 'document' ? '📄 Documento' : '📋 Código PC';
        toast.success(`✅ ${typeText} encontrado! Tempo: ${result.executionTime}ms`);
        
        console.log(`📊 Busca universal: ${typeText} via ${result.searchSource} (${result.searchMethod})`);
      } else {
        const typeText = result.searchType === 'document' ? 'CNPJ/CPF' : 'Código PC';
        toast.error(`❌ ${typeText} não encontrado: ${result.message}`);
      }
      
      return result;
    } catch (error) {
      console.error('❌ Erro na busca universal:', error);
      toast.error('Erro na busca universal');
      return null;
    } finally {
      setLoading(prev => ({ ...prev, universal: false }));
    }
  };

  // Buscar em APIs externas (método original)
  const searchExternalApis = async (cobrancaId) => {
    if (!cobrancaId) {
      toast.warning('Digite um ID de cobrança');
      return;
    }

    try {
      const results = await searchExternalApis(cobrancaId);
      console.log('Resultados das APIs externas:', results);
      return results;
    } catch (error) {
      console.error('Erro ao buscar em APIs externas:', error);
      toast.error('Erro ao buscar em APIs externas');
    }
  };


  // Limpar resultados
  const clearResults = (apiKey = null) => {
    if (apiKey) {
      setSearchResults(prev => {
        const newResults = { ...prev };
        delete newResults[apiKey];
        return newResults;
      });
      setExpandedResults(prev => {
        const newExpanded = { ...prev };
        delete newExpanded[apiKey];
        return newExpanded;
      });
    } else {
      setSearchResults({});
      setExpandedResults({});
    }
  };

  // Alternar expansão de resultado
  const toggleExpandedResult = (apiKey) => {
    setExpandedResults(prev => ({
      ...prev,
      [apiKey]: !prev[apiKey]
    }));
  };

  return {
    // Estados
    searchInputs,
    searchResults,
    loading,
    expandedResults,
    
    // Funções originais
    setSearchInputs,
    setSearchResults,
    searchCobranca,
    searchAllApis,
    searchExternalApis,
    clearResults,
    toggleExpandedResult,
    
    // Novas funções de busca inteligente
    searchHybrid,
    searchUniversalTerm
  };
};
